import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type InstantTooltipProps = {
  label: string
  children: React.ReactElement
  /** When false, render children only (e.g. disabled controls with no hint). */
  enabled?: boolean
}

/** Hover/focus tooltip with no show delay (native `title` waits ~1s in browsers). */
export default function InstantTooltip({
  label,
  children,
  enabled = true,
}: InstantTooltipProps) {
  const tooltipId = useId()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    setPosition({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    })
  }, [])

  const show = useCallback(() => {
    updatePosition()
    setVisible(true)
  }, [updatePosition])

  const hide = useCallback(() => {
    setVisible(false)
  }, [])

  useEffect(() => {
    if (!visible) return
    const onScrollOrResize = () => updatePosition()
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [visible, updatePosition])

  if (!enabled) {
    return children
  }

  const child = React.Children.only(children)

  return (
    <>
      <div
        ref={anchorRef}
        className="inline-flex"
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {React.cloneElement(child, {
          'aria-describedby': visible ? tooltipId : undefined,
          onFocus: (event: React.FocusEvent) => {
            child.props.onFocus?.(event)
            show()
          },
          onBlur: (event: React.FocusEvent) => {
            child.props.onBlur?.(event)
            hide()
          },
        })}
      </div>
      {visible &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            style={{ top: position.top, left: position.left }}
            className="pointer-events-none fixed z-[100] max-w-[220px] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md border border-border-default bg-surface-default px-2 py-1 text-caption text-text-primary shadow-sm"
          >
            {label}
          </div>,
          document.body,
        )}
    </>
  )
}
